/**
 * Utility functions for the Social Media Control Center (SMCC)
 */

import { type AnalyticsPeriod, ANALYTICS_PERIODS, type SMCCPlatformId, PLATFORM_CONFIG } from '../constants/smcc';

/**
 * Format a number with K/M suffixes for display
 */
export function formatNumber(num: number): string {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toLocaleString();
}

/**
 * Get date range from analytics period
 */
export function getPeriodDateRange(period: AnalyticsPeriod): { startDate: Date; endDate: Date } {
  const now = new Date();
  const periodConfig = ANALYTICS_PERIODS.find(p => p.value === period);
  const hours = periodConfig?.hours || 24 * 7; // Default to 7 days

  const startDate = new Date(now.getTime() - hours * 60 * 60 * 1000);
  return { startDate, endDate: now };
}

/**
 * Format date range for API calls (YYYY-MM-DD)
 */
export function formatDateForAPI(date: Date): string {
  return date.toISOString().split('T')[0];
}

/**
 * Get ISO date strings for a period
 */
export function getPeriodDatesForAPI(period: AnalyticsPeriod): { start_date: string; end_date: string } {
  const { startDate, endDate } = getPeriodDateRange(period);
  return {
    start_date: formatDateForAPI(startDate),
    end_date: formatDateForAPI(endDate),
  };
}

/**
 * Validate content against platform limits
 */
export interface ContentValidation {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export function validateContentForPlatform(
  platform: SMCCPlatformId,
  body: string,
  hashtags: string[],
  mediaCount: number
): ContentValidation {
  const config = PLATFORM_CONFIG[platform];
  const errors: string[] = [];
  const warnings: string[] = [];

  // Calculate full text length including hashtags
  const hashtagText = hashtags.map(h => `#${h}`).join(' ');
  const fullText = hashtags.length > 0 ? `${body}\n\n${hashtagText}` : body;

  // Check character limit
  if (fullText.length > config.maxLength) {
    errors.push(`Content exceeds ${config.name}'s ${config.maxLength} character limit (${fullText.length} chars)`);
  } else if (fullText.length > config.maxLength * 0.9) {
    warnings.push(`Content is approaching ${config.name}'s character limit`);
  }

  // Check hashtag limit
  if (config.maxHashtags > 0 && hashtags.length > config.maxHashtags) {
    errors.push(`Too many hashtags for ${config.name} (max ${config.maxHashtags}, got ${hashtags.length})`);
  }

  // Check media limits
  if (config.supportsMedia && config.maxImages > 0 && mediaCount > config.maxImages) {
    errors.push(`Too many images for ${config.name} (max ${config.maxImages})`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate content for all selected platforms
 */
export function validateContentForPlatforms(
  platforms: SMCCPlatformId[],
  body: string,
  hashtags: string[],
  mediaCount: number
): Map<SMCCPlatformId, ContentValidation> {
  const results = new Map<SMCCPlatformId, ContentValidation>();

  for (const platform of platforms) {
    results.set(platform, validateContentForPlatform(platform, body, hashtags, mediaCount));
  }

  return results;
}

/**
 * Check if content is valid for all platforms
 */
export function isContentValidForAllPlatforms(
  validationResults: Map<SMCCPlatformId, ContentValidation>
): boolean {
  for (const result of validationResults.values()) {
    if (!result.isValid) return false;
  }
  return true;
}

/**
 * Get all validation errors across platforms
 */
export function getAllValidationErrors(
  validationResults: Map<SMCCPlatformId, ContentValidation>
): string[] {
  const errors: string[] = [];
  for (const result of validationResults.values()) {
    errors.push(...result.errors);
  }
  return errors;
}

/**
 * Format a relative time string (e.g., "2 hours ago")
 */
export function formatRelativeTime(date: Date | string): string {
  const now = new Date();
  const then = typeof date === 'string' ? new Date(date) : date;
  const diffMs = now.getTime() - then.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return then.toLocaleDateString();
}

/**
 * Truncate text to a max length with ellipsis
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

/**
 * Extract hashtags from a text string
 */
export function extractHashtags(text: string): string[] {
  const matches = text.match(/#[\w]+/g) || [];
  return matches.map(tag => tag.slice(1)); // Remove the # prefix
}

/**
 * Build content body with hashtags appended
 */
export function buildContentWithHashtags(body: string, hashtags: string[]): string {
  if (hashtags.length === 0) return body;
  const hashtagText = hashtags.map(h => `#${h}`).join(' ');
  return `${body}\n\n${hashtagText}`;
}

/**
 * Parse media type from URL
 */
export function getMediaTypeFromUrl(url: string): 'image' | 'video' {
  const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mkv'];
  const lowerUrl = url.toLowerCase();

  for (const ext of videoExtensions) {
    if (lowerUrl.endsWith(ext)) return 'video';
  }

  return 'image';
}
